import { Link, useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import type {
  MarketingEmailTemplate,
  MarketingEmailTemplateContentType,
} from "@/react/types/MarketingEmailTemplates";
import { Button } from "@circle-react-uikit/Button";
import { Typography } from "@circle-react-uikit/Typography";
import { BroadcastTipTapReadonlyView } from "../EmailPreview/BroadcastTipTapReadonlyView";
import { templateLocaleNamespace } from "../UpsertTemplateModal/UpdateTemplateContent";
import { CardOptions } from "./CardOptions";
import { useDeleteTemplate } from "./hooks/useDeleteTemplate";
import { useRenameTemplate } from "./hooks/useRenameTemplate";

export function TemplateCard({
  template,
  onApply,
}: {
  template: MarketingEmailTemplate;
  onApply: (
    templateId: number,
    contentType: MarketingEmailTemplateContentType,
  ) => void;
}) {
  const history = useHistory();
  const { deleteModalElement, openDeleteModal } = useDeleteTemplate({
    template,
  });
  const { renameTemplateElement, openRenameTemplateModal } = useRenameTemplate({
    template,
  });

  return (
    <>
      <Link
        to={{
          pathname: `/templates/${template.id}`,
          state: {
            template,
          },
        }}
        className="group flex flex-col gap-3"
        title={template.name}
      >
        <div className="border-primary relative aspect-[0.8] size-full overflow-hidden rounded-lg border p-2 shadow-sm after:absolute after:inset-0 after:z-10 after:transition-colors after:duration-300 group-hover:after:bg-black/80">
          <div className="h-full [zoom:0.6]">
            <BroadcastTipTapReadonlyView content={template.content} />
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- This is not an interactive component. It just needs to block click events from propagating upwards. Does not take any action */}
          <div
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <div className="absolute right-3 top-3 z-30 hidden group-hover:block">
              <CardOptions
                openDeleteModal={openDeleteModal}
                openRenameTemplateModal={openRenameTemplateModal}
              />
            </div>
            <div className="absolute inset-0 z-20 hidden items-center justify-center group-hover:flex">
              <div className="flex w-full flex-col gap-y-3 px-12">
                <Button
                  variant="secondary"
                  type="button"
                  large
                  full
                  onClick={() => onApply(template.id, template.content_type)}
                  className="!bg-white !text-gray-800"
                >
                  {t([templateLocaleNamespace, "apply"])}
                </Button>
                <Button
                  variant="secondary"
                  large
                  full
                  type="button"
                  className="border-transparent !bg-transparent !text-white"
                  onClick={() => {
                    history.push({
                      pathname: `/templates/${template.id}`,
                      state: {
                        template,
                      },
                    });
                  }}
                >
                  {t([templateLocaleNamespace, "preview"])}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <Typography.LabelSm weight="semibold" truncate>
          {template.name}
        </Typography.LabelSm>
      </Link>
      {deleteModalElement}
      {renameTemplateElement}
    </>
  );
}
